import { Component } from '@mycolorway/tao';
import { throttle } from 'next/javascripts/modules/utils';
import { closest } from 'next/javascripts/modules/dom';
import resourceSubject from 'next/javascripts/reactivity/resource-subject';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import 'next/stylesheets/components/grid/group.scss';

export default Component('tr-grid-group', {
  mixins: [eventListenerMixin],

  properties: {
    key: String,
    value: String,
    verticalScrollableSelector: String,

    expandable: {
      type: Boolean,
      default: true,
    },

    expanded: {
      type: Boolean,
      default: true,
      observer: 'expandedChanged',
    },

    sortable: {
      type: Boolean,
      default: false,
    },

    headerSticky: {
      type: Boolean,
      default: false,
    },
  },

  connected() {
    this.header = this.querySelector('.group-header');
    this.footer = this.querySelector('.group-footer');

    this.initVerticalScroll();
    this.initExpanded();
    this.initSummary();
    this.initFooterNewRow();

    this.gridSubscription = resourceSubject('tr-grid').subscribe({
      next: (event, payload) => {
        if (event === 'columnResized') {
          this.updateHeaderAndFoorter({ key: payload.syncKey, width: payload.width });
        }
        if (event === 'stickyColumnRangeChagned') {
          const cell = this.querySelector('tr-grid-resize-cell.grid-sticky-cell');
          if (cell) {
            cell.max = payload.max;
            cell.min = payload.min;
          }
        }
      },
    });

    this.listenTo(this.querySelector('.expandable-icon'), 'click', () => {
      if (!this.expandable) return;
      this.expanded = !this.expanded;
    });

    this.listenTo(this, {
      click: this.handleActionClick.bind(this),
      'tao:connected': () => {
        this.namespacedTrigger('afterConnected', { bubbles: true });
      },
    });
  },

  ready() {
    this.summary();
  },

  disconnected() {
    this.stopListening();
    if (this.gridSubscription) this.gridSubscription.unsubscribe();
  },

  initVerticalScroll() {
    if (!this.header) return;

    this.scrollContainer = window;
    if (this.verticalScrollableSelector) {
      this.scrollContainer = document.querySelector(this.verticalScrollableSelector);
    }

    this.header.querySelector('.group-header-placeholder').style.height = `${
      this.header.getBoundingClientRect().height
    }px`;
    this.headerContentStyle = this.header.querySelector('.group-header-content').style;

    this.listenTo(this.scrollContainer, {
      scroll: throttle(this.handleVerticalScroll.bind(this), 100),
    });
  },

  initExpanded() {
    if (this.expandable) {
      const storedExpanded = localStorage.getItem(this.getExpandedStorageKey());
      this.expanded = storedExpanded != null ? storedExpanded : this.expanded;
    }
  },

  initSummary() {
    this.summaryRow = this.footer.querySelector('.group-summary-row');
    if (!this.summaryRow) return;

    this.summarySetting = {
      data: {},
      storeKey: `tr-grid-group/summary/${this.value}`,
      init() {
        this.data = JSON.parse(localStorage.getItem(this.storeKey)) || {};
      },
      set(key, value) {
        this.data[key] = value;
        localStorage.setItem(this.storeKey, JSON.stringify(this.data));
      },
      get(key) {
        return this.data[key];
      },
    };
    this.summarySetting.init();

    this.summaryCells = Array.from(this.summaryRow.querySelectorAll('.grid-summary-cell'));
    this.summaryCells.forEach((cell) => {
      const funName = this.summarySetting.get(cell.target);
      // eslint-disable-next-line no-param-reassign
      if (funName) cell.funcName = funName;
    });

    this.listenTo(this, {
      'tr-grid-summary-cell:change': (e) => {
        const cell = e.target;
        this.summaryOne(cell);
        this.summarySetting.set(cell.target, cell.funcName);
      },
    });
  },

  initFooterNewRow() {
    this.footerNewRow = this.footer.querySelector('.group-new-row');
    if (!this.footerNewRow) return;

    this.listenTo(this.footerNewRow.querySelector('.new-row'), 'click', (e) => {
      e.stopPropagation();
      this.namespacedTrigger('footerNewRow', {
        detail: { footer: true, target: e.currentTarget },
        bubbles: true,
      });
    });
  },

  expandedChanged(expanded) {
    if (expanded) {
      localStorage.removeItem(this.getExpandedStorageKey());
    } else {
      localStorage.setItem(this.getExpandedStorageKey(), expanded);
    }

    if (this.headerSticky && !expanded) {
      this.scrollContainer.scrollTop = this.offsetTop - 1;
    }
  },

  handleActionClick(event) {
    const target = closest(event.target, '.actions .action', this);
    if (target) {
      this.namespacedTrigger('actionClick', {
        detail: { target },
        bubbles: true,
      });
    }
  },

  handleVerticalScroll() {
    if (this.verticalContainerTop == null) {
      this.verticalContainerTop = this.scrollContainer === window
        ? 0 : this.scrollContainer.getBoundingClientRect().top;
    }

    const headerRect = this.header.getBoundingClientRect();
    const { bottom: groupBottom } = this.getBoundingClientRect();
    const groupGap = parseInt(this.style.paddingTop, 10) || 0;

    let sticky = groupBottom + groupGap + headerRect.height / 2 > this.verticalContainerTop
      && headerRect.top - headerRect.height / 2 < this.verticalContainerTop;
    if (sticky && this.scrollContainer.scrollTop === 0
      && headerRect.top === this.verticalContainerTop) {
      sticky = false;
    }

    this.headerSticky = sticky;
    this.headerContentStyle.top = sticky ? `${this.verticalContainerTop}px` : '';
  },

  getExpandedStorageKey() {
    return `tr-grid-group/expanded/${this.key}/${this.value}`;
  },

  updateGroupTitleWidth(width) {
    if (!this.header) return;

    this.stickyColStyle = this.stickyColStyle || this.header
      .querySelector('.grid-sticky-cell').style;
    this.stickyColPlaceholderStyle = this.stickyColPlaceholderStyle || this.header
      .querySelector('.grid-sticky-cell-placeholder').style;

    this.stickyColStyle.width = `${width}px`;
    this.stickyColPlaceholderStyle.width = `${width}px`;
  },

  updateHeaderAndFoorter({ key, width }) {
    if (key === 'stickyCell') {
      this.updateGroupTitleWidth(width);
    }

    if (this.summaryRow) {
      if (key === 'stickyCell') {
        this.summaryRow.querySelector('.grid-sticky-cell').style.width = `${width}px`;
        this.summaryRow.querySelector('.grid-sticky-cell-placeholder').style.width = `${width}px`;
      } else {
        const cell = this.summaryRow.querySelector(`[target="${key}"]`);
        if (cell) cell.style.width = `${width}px`;
      }
    }
  },

  summaryOne(summary) {
    if (summary.isCalculatable()) {
      const sel = `.group-content .grid-cell[key="${summary.target}"]:not(.skip-summary)`;
      const cells = Array.from(this.querySelectorAll(sel));
      summary.calculate(cells);
    }
  },

  summary() {
    if (!this.summaryCells) return;

    const cells = Array.from(this.querySelectorAll('.group-content .grid-cell:not(.skip-summary)'));
    this.summaryCells.forEach((summary) => {
      if (summary.isCalculatable()) {
        summary.calculate(cells.filter(cell => cell.key === summary.target));
      }
    });
  },

  updateSummaryFunc(target, funcName) {
    if (!this.summaryCells) return;

    const cell = this.summaryCells.find(item => item.target === target);
    if (!cell) return;
    cell.selectFuncName(funcName);
    this.summarySetting.set(cell.target, cell.funcName);
    this.summaryOne(cell);
  },
});
